import { useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { StatusDot } from '@/components/ui/StatusDot';
import { EditorialImage } from '@/components/editorial/EditorialImage';
import type { FeedItem } from '@/modules/feed/composer';
import type { MockProfile } from '@/data/mockProfiles';
import type { VipProfile } from '@/modules/vip/types';
import type { TvSeries, TvCreator } from '@/modules/tv/types';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/cn';

interface MobileSwipeFeedProps {
  items: FeedItem[];
}

/**
 * TikTok-style vertical full-screen swipe feed for mobile.
 *
 * Each item fills the viewport minus the kit header. The Orb floats over
 * the thumb corner; nothing here draws a tab bar.
 *
 * Desktop continues using the grid layout in FeedPage — this component is
 * mounted in md:hidden territory only.
 */
export const MobileSwipeFeed = ({ items }: MobileSwipeFeedProps) => (
  <div
    className="hottr-no-scrollbar h-[calc(100dvh-var(--hottr-header-h-mobile,56px))] w-full overflow-y-auto md:hidden"
    style={{
      scrollSnapType: 'y mandatory',
      scrollSnapStop: 'always',
      WebkitOverflowScrolling: 'touch',
    }}
  >
    {items.map((item, i) => (
      <SwipeItem key={item.id} item={item} index={i} />
    ))}
    {/* Tail card — end of feed */}
    <section
      className="relative flex h-[calc(100dvh-var(--hottr-header-h-mobile,56px))] w-full snap-start items-center justify-center bg-ink px-8 text-center"
      style={{ scrollSnapAlign: 'start' }}
    >
      <div>
        <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
          — End of feed
        </p>
        <p className="mt-3 font-display text-3xl font-black uppercase tracking-display">
          Refreshing in an hour<span className="text-pink">.</span>
        </p>
        <p className="mt-3 text-sm text-text-secondary">
          The room cycles<span className="text-pink">.</span>
        </p>
      </div>
    </section>
  </div>
);

/* ============================================================
   SWIPE ITEM
   ============================================================ */

const SwipeItem = ({ item, index }: { item: FeedItem; index: number }) => {
  if (item.kind === 'app_profile')
    return <AppSwipe profile={item.profile} index={index} />;
  if (item.kind === 'vip_profile')
    return <VipSwipe profile={item.profile} index={index} />;
  if (item.kind === 'tv_series')
    return <TvSeriesSwipe series={item.series} index={index} />;
  if (item.kind === 'tv_creator')
    return <TvCreatorSwipe creator={item.creator} index={index} />;
  return null;
};

/* ============================================================
   APP PROFILE — full-bleed photo, gallery dots, action rail right
   ============================================================ */

const AppSwipe = ({ profile, index }: { profile: MockProfile; index: number }) => {
  const navigate = useNavigate();
  const [photoIndex, setPhotoIndex] = useState(0);
  const photos = profile.photos.length > 0 ? profile.photos : [''];
  const hasGallery = photos.length > 1;

  const cyclePhoto = (e: MouseEvent) => {
    if (!hasGallery) return;
    e.stopPropagation();
    setPhotoIndex((i) => (i + 1) % photos.length);
  };

  return (
    <section
      className="relative isolate h-[calc(100dvh-var(--hottr-header-h-mobile,56px))] w-full snap-start overflow-hidden"
      style={{ scrollSnapAlign: 'start' }}
    >
      {/* Photo */}
      <EditorialImage
        scene={profile.scene}
        seed={profile.sceneSeed + photoIndex * 3}
        photoUrl={photos[photoIndex]}
        aspect="aspect-auto"
        className="h-full"
      />

      {/* Tap-right zone — cycle photos */}
      {hasGallery && (
        <button
          type="button"
          aria-label="Next photo"
          onClick={cyclePhoto}
          className="absolute inset-y-0 right-0 z-10 w-1/3"
        />
      )}

      {/* Top fade */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-32"
        style={{
          background: 'linear-gradient(180deg, rgba(5,6,8,0.55) 0%, transparent 100%)',
        }}
      />

      {/* Bottom scrim */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
        style={{
          background:
            'linear-gradient(180deg, rgba(5,6,8,0) 0%, rgba(5,6,8,0.55) 50%, rgba(5,6,8,0.95) 100%)',
        }}
      />

      {/* TOP — gallery dots + live */}
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-3 px-5 pt-4">
        <span className="inline-flex items-center gap-1">
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Photo ${i + 1}`}
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex(i);
              }}
              className={cn(
                'h-1 transition-all duration-fast ease-signal',
                i === photoIndex
                  ? 'w-8 bg-pink shadow-glow-soft'
                  : 'w-5 bg-white/30',
              )}
            />
          ))}
        </span>
        {profile.presence === 'live' && (
          <span className="inline-flex items-center gap-1.5 border border-pink/40 bg-ink/85 px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur">
            <StatusDot status="live" size="h-1.5 w-1.5" />
            Live
          </span>
        )}
      </div>

      {/* Right-side action rail */}
      <ActionRail
        actions={[
          { icon: 'whatshot', label: 'Pulse', onClick: () => {} },
          { icon: 'bookmark_add', label: 'Save', onClick: () => {} },
          {
            icon: 'forum',
            label: 'Signal',
            onClick: () => navigate(ROUTES.profile(profile.handle)),
            pink: true,
          },
          { icon: 'ios_share', label: 'Share', onClick: () => {} },
        ]}
      />

      {/* Bottom info */}
      <footer className="absolute inset-x-0 bottom-0 z-20 pr-20 pb-6 pl-5">
        <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          {profile.location} · {profile.distance}
          {profile.age && ` · ${profile.age}`}
        </p>
        <Link
          to={ROUTES.profile(profile.handle)}
          className="mt-1.5 block font-display font-black uppercase leading-[0.88] tracking-display text-text-primary"
          style={{
            fontSize: 'clamp(36px, 9vw, 56px)',
            letterSpacing: '-0.05em',
          }}
        >
          {profile.handle}<span className="text-pink">.</span>
        </Link>
        <p className="mt-3 line-clamp-2 text-sm text-text-secondary">
          {profile.mood}
        </p>
        <p className="mt-3 line-clamp-1 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          {profile.height}
          <span className="mx-1.5 text-pink">·</span>
          {profile.body}
          <span className="mx-1.5 text-pink">·</span>
          {profile.position}
        </p>
      </footer>

      {/* Subtle swipe-up hint on the first card */}
      {index === 0 && (
        <span className="pointer-events-none absolute inset-x-0 bottom-2 z-10 text-center font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
          ↑ Swipe up
        </span>
      )}
    </section>
  );
};

/* ============================================================
   VIP CROSS-PROMO — B&W full-screen
   ============================================================ */

const VipSwipe = ({ profile, index }: { profile: VipProfile; index: number }) => {
  const navigate = useNavigate();
  return (
    <section
      className="relative isolate h-[calc(100dvh-var(--hottr-header-h-mobile,56px))] w-full snap-start overflow-hidden"
      style={{ scrollSnapAlign: 'start' }}
    >
      <EditorialImage
        scene={profile.scene}
        seed={profile.sceneSeed}
        photoUrl={profile.photos[0]}
        aspect="aspect-auto"
        className="h-full"
        mono
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-32"
        style={{
          background: 'linear-gradient(180deg, rgba(5,6,8,0.65) 0%, transparent 100%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3"
        style={{
          background:
            'linear-gradient(180deg, rgba(5,6,8,0) 0%, rgba(5,6,8,0.55) 40%, rgba(5,6,8,0.95) 100%)',
        }}
      />

      {/* Surface badge */}
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-3 px-5 pt-4">
        <span className="inline-flex items-center gap-1.5 border border-pink/40 bg-ink/85 px-2.5 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink shadow-glow-soft backdrop-blur">
          ★ HOTTR<span className="text-pink">.</span>
          <span className="font-light italic normal-case">vip</span>
        </span>
        {profile.verification === 'verified' && (
          <span className="inline-flex items-center gap-1 border border-pink/40 bg-ink/85 px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur">
            <Icon name="verified" filled size={11} />
            Verified
          </span>
        )}
      </div>

      <ActionRail
        actions={[
          { icon: 'star_border', label: 'Save', onClick: () => {} },
          {
            icon: 'arrow_forward',
            label: 'Open',
            onClick: () => navigate(ROUTES.vip.profile(profile.handle)),
            pink: true,
          },
          { icon: 'ios_share', label: 'Share', onClick: () => {} },
        ]}
      />

      <footer className="absolute inset-x-0 bottom-0 z-20 pr-20 pb-6 pl-5">
        <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          <span className="text-pink">No.</span> {String(profile.memberNo).padStart(3, '0')}
          <span className="mx-2 text-pink">·</span>
          {profile.suburb}
        </p>
        <Link
          to={ROUTES.vip.profile(profile.handle)}
          className="mt-1.5 block font-display font-black uppercase leading-[0.88] tracking-display text-text-primary"
          style={{
            fontSize: 'clamp(32px, 8vw, 52px)',
            letterSpacing: '-0.05em',
          }}
        >
          {profile.displayName}<span className="text-pink">.</span>
        </Link>
        <p className="mt-3 line-clamp-1 text-sm italic text-text-secondary">
          &ldquo;{profile.mood}&rdquo;
        </p>
        <p className="mt-3 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
          Featured on the directory
        </p>
      </footer>
      // hint for index unused intentionally
      {index < 0 && null}
    </section>
  );
};

/* ============================================================
   TV SERIES CROSS-PROMO
   ============================================================ */

const TvSeriesSwipe = ({ series, index }: { series: TvSeries; index: number }) => {
  const navigate = useNavigate();
  return (
    <section
      className="relative isolate h-[calc(100dvh-var(--hottr-header-h-mobile,56px))] w-full snap-start overflow-hidden"
      style={{ scrollSnapAlign: 'start' }}
    >
      <EditorialImage
        scene={series.scene}
        seed={series.sceneSeed}
        photoUrl={series.heroUrl}
        aspect="aspect-auto"
        className="h-full"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-32"
        style={{
          background: 'linear-gradient(180deg, rgba(5,6,8,0.55) 0%, transparent 100%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3"
        style={{
          background:
            'linear-gradient(180deg, rgba(5,6,8,0) 0%, rgba(5,6,8,0.55) 40%, rgba(5,6,8,0.95) 100%)',
        }}
      />

      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-3 px-5 pt-4">
        <span className="inline-flex items-center gap-1.5 border border-pink/40 bg-ink/85 px-2.5 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink shadow-glow-soft backdrop-blur">
          ★ HOTTR<span className="text-pink">.</span>
          <span className="font-light italic normal-case">tv</span>
        </span>
        <span className="inline-flex items-center gap-1.5 border border-white/[0.10] bg-ink/85 px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-text-secondary backdrop-blur">
          {series.episodes.length} ep · {series.rating}
        </span>
      </div>

      <ActionRail
        actions={[
          {
            icon: 'play_arrow',
            label: 'Watch',
            filled: true,
            onClick: () =>
              navigate(ROUTES.tv.watch(series.episodes[0]?.id ?? series.id)),
            pink: true,
          },
          { icon: 'bookmark_add', label: 'Save', onClick: () => {} },
          { icon: 'ios_share', label: 'Share', onClick: () => {} },
        ]}
      />

      <footer className="absolute inset-x-0 bottom-0 z-20 pr-20 pb-6 pl-5">
        <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          {series.eyebrow}
        </p>
        <Link
          to={ROUTES.tv.series(series.slug)}
          className="mt-1.5 block font-display font-black uppercase leading-[0.86] tracking-display text-text-primary"
          style={{
            fontSize: 'clamp(34px, 9vw, 56px)',
            letterSpacing: '-0.05em',
          }}
        >
          {series.title}<span className="text-pink">.</span>
        </Link>
        <p className="mt-3 line-clamp-2 text-sm text-text-secondary">
          <span className="text-text-primary">{series.tagline}</span>
        </p>
        <p className="mt-3 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
          Watching on HOTTR<span className="text-pink">.</span>tv
        </p>
      </footer>
      {index < 0 && null}
    </section>
  );
};

/* ============================================================
   TV CREATOR CROSS-PROMO
   ============================================================ */

const TvCreatorSwipe = ({ creator, index }: { creator: TvCreator; index: number }) => {
  const navigate = useNavigate();
  return (
    <section
      className="relative isolate h-[calc(100dvh-var(--hottr-header-h-mobile,56px))] w-full snap-start overflow-hidden bg-night"
      style={{ scrollSnapAlign: 'start' }}
    >
      <img
        src={creator.portraitUrl}
        alt=""
        className="hottr-matte-editorial absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-32"
        style={{
          background: 'linear-gradient(180deg, rgba(5,6,8,0.55) 0%, transparent 100%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3"
        style={{
          background:
            'linear-gradient(180deg, rgba(5,6,8,0) 0%, rgba(5,6,8,0.55) 40%, rgba(5,6,8,0.95) 100%)',
        }}
      />

      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-3 px-5 pt-4">
        <span className="inline-flex items-center gap-1.5 border border-pink/40 bg-ink/85 px-2.5 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink shadow-glow-soft backdrop-blur">
          ★ HOTTR<span className="text-pink">.</span>
          <span className="font-light italic normal-case">tv</span>
        </span>
        {creator.isVerified && (
          <span className="inline-flex items-center gap-1 border border-pink/40 bg-ink/85 px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur">
            <Icon name="verified" filled size={11} />
            Verified
          </span>
        )}
      </div>

      <ActionRail
        actions={[
          { icon: 'person_add', label: 'Subscribe', onClick: () => {}, pink: true },
          {
            icon: 'arrow_forward',
            label: 'Channel',
            onClick: () => navigate(ROUTES.tv.creator(creator.handle)),
          },
          { icon: 'ios_share', label: 'Share', onClick: () => {} },
        ]}
      />

      <footer className="absolute inset-x-0 bottom-0 z-20 pr-20 pb-6 pl-5">
        <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          Creator · {creator.role}
          <span className="mx-2 text-pink">·</span>
          {creator.subscribers >= 1000
            ? `${(creator.subscribers / 1000).toFixed(1)}k subs`
            : `${creator.subscribers} subs`}
        </p>
        <Link
          to={ROUTES.tv.creator(creator.handle)}
          className="mt-1.5 block font-display font-black uppercase leading-[0.88] tracking-display text-text-primary"
          style={{
            fontSize: 'clamp(28px, 7.5vw, 44px)',
            letterSpacing: '-0.04em',
          }}
        >
          {creator.name}<span className="text-pink">.</span>
        </Link>
        <p className="mt-3 line-clamp-2 text-sm text-text-secondary">
          {creator.bio}
        </p>
      </footer>
      {index < 0 && null}
    </section>
  );
};

/* ============================================================
   ACTION RAIL — right-side affordances on mobile
   ============================================================ */

interface ActionItem {
  icon: string;
  label: string;
  onClick: () => void;
  filled?: boolean;
  pink?: boolean;
}

const ActionRail = ({ actions }: { actions: ActionItem[] }) => (
  <div className="absolute right-3 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-3">
    {actions.map((a) => (
      <button
        key={a.label}
        type="button"
        onClick={a.onClick}
        aria-label={a.label}
        className={cn(
          'flex h-12 w-12 items-center justify-center border backdrop-blur',
          'transition-[border-color,background,color] duration-fast ease-signal',
          a.pink
            ? 'border-pink/50 bg-pink/15 text-pink shadow-glow-soft hover:bg-pink/25'
            : 'border-white/[0.15] bg-ink/55 text-text-secondary hover:border-pink/40 hover:text-text-primary',
        )}
      >
        <Icon name={a.icon} size={20} filled={a.filled} />
      </button>
    ))}
  </div>
);
